import { Upload, X } from "lucide-react";
import CustomInput from "../../shared/CustomInput";
import CustomArea from "../../shared/CustomArea";
import { useRef, useState } from "react";
import Button from "../../shared/Button";
import { useFormatAmount } from "../../hooks/useFormatAmount";

const ManualTransaction = ({
  closeManualTransaction,
  formData,
  setFormData,
  balance,
  uploadedFile,
  setUploadedFile,
  handleContinue,
}) => {
  const formatAmount = useFormatAmount();

  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    let errorMsg = "";
    switch (name) {
      case "amount":
        if (!value) errorMsg = "Amount is required";
        else if (isNaN(value) || Number(value) <= 0)
          errorMsg = "Enter a valid amount";
        break;
      case "date":
        if (!value) errorMsg = "Date is required";
        break;
      case "method":
        if (!value) errorMsg = "Please select a payment method";
        break;
      case "reference":
        if (!value) errorMsg = "Reference code is required";
        break;
      case "receipt":
        if (!value)
          errorMsg = "Transaction receipt document upload is required";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const selectRef = useRef(null);
  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    let newErrors = {};

    // Check Amount
    if (!formData.amount) {
      newErrors.amount = "Amount is required";
    } else if (isNaN(formData.amount) || Number(formData.amount) <= 0) {
      newErrors.amount = "Enter a valid positive amount";
    }

    // Check Date
    if (!formData.date) {
      newErrors.date = "Payment date is required";
    }

    // Check Method
    if (!formData.method) {
      newErrors.method = "Please select a payment method";
    }

    // Check Reference
    if (!formData.reference) {
      newErrors.reference = "Payment reference is required";
    }

    // CRITICAL: Force validation check on state binding
    if (!uploadedFile) {
      newErrors.receipt = "Transaction receipt document upload is required";
    }

    // Update error state
    setErrors(newErrors);

    // Return true if the newErrors object is empty
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedFile(file);
  };

  const handleSubmit = () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    console.log("first");
    handleContinue();
  };

  return (
    <div
      onClick={closeManualTransaction}
      className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[850px] rounded-[10px] p-[20px] relative bg-white"
      >
        <div
          onClick={closeManualTransaction}
          className="absolute top-[0px] cursor-pointer h-[40px] w-[40px] bg-[#D63B3B] rounded-tr-[10px] rounded-bl-[20px] right-[0px] flex items-center justify-center"
        >
          <X color="#ffffff" />
        </div>

        <div className="w-full h-full p-[15px]">
          <p className="text-[#000000] text-[20px] font-semibold">
            Record Manual Payment
          </p>
          <p className="text-[#737373] text-[12px] mb-[20px] font-normal">
            Use the form below to record a payment made outside the system.
          </p>
          <div className="w-full mb-[20px]">
            <p className="text-[#000000] text-[13px] font-medium">
              Outstanding loan balance
            </p>
            <div className="h-[48px] text-[#F3F3F3] w-[200px] rounded-[8px] bg-[#1E5D97] flex items-center justify-center">
              {formatAmount(balance)}
            </div>
          </div>

          <div className="flex items-start mb-[20px] w-full justify-between">
            <div className="w-[49%] flex flex-col gap-[5px]">
              <label className="font-medium text-[#000000] text-[13px]">
                Manual Payment Amount
              </label>
              <CustomInput
                name="amount"
                type="text"
                placeholder="Enter manual payment amount"
                value={formData.amount}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors.amount && (
                <span className="text-red-500 text-[11px]">
                  {errors.amount}
                </span>
              )}
            </div>

            <div className="w-[49%] flex flex-col gap-[5px]">
              <label className="font-medium text-[#000000] text-[13px]">
                Payment Date
              </label>
              <CustomInput
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors.date && (
                <span className="text-red-500 text-[11px]">{errors.date}</span>
              )}
            </div>
          </div>

          <div className="flex items-start w-full mb-[20px] justify-between">
            <div className="w-[49%] flex flex-col gap-[5px]">
              <label className="font-medium text-[#000000] text-[13px]">
                Payment Method
              </label>
              <select
                ref={selectRef}
                name="method"
                value={formData.method}
                onBlur={handleBlur}
                onChange={(e) => {
                  handleChange(e);
                  e.target.blur();
                }}
                className={`h-[45px] w-full text-[14px] rounded-[2px] px-[10px] border bg-white cursor-pointer focus:outline-none transition-colors ${
                  errors.method
                    ? "border-red-500"
                    : "border-[#D9D9D9] focus:border-[#1E5D97]"
                }`}
              >
                <option value="" disabled>
                  Select payment method
                </option>
                <option value="cash">Cash</option>
                <option value="mpesa_till">M-Pesa Till</option>
                <option value="mpesa_paybill">M-Pesa Paybill</option>
                <option value="pesapal">Pesapal</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </select>
              {errors.method && (
                <span className="text-red-500 text-[11px] mt-1">
                  {errors.method}
                </span>
              )}
            </div>

            <div className="w-[49%] flex flex-col gap-[5px]">
              <label className="font-medium text-[#000000] text-[13px]">
                Payment Reference
              </label>
              <CustomInput
                name="reference"
                type="text"
                placeholder="Enter payment reference"
                value={formData.reference}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors.reference && (
                <span className="text-red-500 text-[11px]">
                  {errors.reference}
                </span>
              )}
            </div>
          </div>

          <div className="w-[100%] flex flex-col mb-[20px] gap-[5px]">
            <label className="font-medium text-[#000000] text-[13px]">
              Notes (Optional)
            </label>
            <CustomArea
              name="notes"
              placeholder="Enter payment notes"
              value={formData.notes}
              onChange={handleChange}
            />
          </div>

          <div>
            <p className="text-[#000000] font-normal text-[13px] mb-2">
              Upload Transaction Receipt *
            </p>

            {/* Injects dynamic border highlights if validation fails */}
            <label className={`border border-dashed rounded-[5px] w-full bg-white p-[10px] flex flex-col justify-center items-center relative cursor-pointer transition-colors ${
              errors.receipt ? "border-red-500 bg-red-50/10" : "border-[#A8A8A8] hover:bg-gray-50"
            }`}>
              <input
                type="file"
                className="hidden"
                accept="image/*,.pdf"
                onChange={handleFileChange}
              />

              <Upload size={30} className={errors.receipt ? "text-red-400 mb-2" : "text-gray-400 mb-2"} />

              <div className="text-center">
                <p className="text-[12px] font-normal text-[#000000]">
                  <span className="font-semibold text-[#1E5D97]">
                    Click to upload
                  </span>{" "}
                  or drag and drop
                </p>
                <p className="text-[10px] text-gray-500 mt-1">
                  PNG, JPG or PDF (max. 10MB)
                </p>
              </div>

              {/* Display file info if uploaded */}
              {uploadedFile && (
                <div className="mt-4 p-2 bg-gray-100 rounded-md w-full flex items-center justify-between" onClick={(e) => e.preventDefault()}>
                  <div className="flex items-center gap-2 overflow-hidden">
                    <p className="text-[11px] font-medium truncate">
                      {uploadedFile.name}
                    </p>
                    <span className="text-[10px] text-gray-400">
                      ({(uploadedFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <X
                    size={14}
                    className="text-red-500 cursor-pointer hover:scale-110 transition-transform"
                    onClick={(e) => {
                      e.stopPropagation(); // Stops the label from triggering click re-upload sequence
                      setUploadedFile(null);
                      validateField("receipt", null); // Updates active warning blocks instantly
                    }}
                  />
                </div>
              )}
            </label>
            {errors.receipt && (
              <span className="text-red-500 text-[11px] mt-1 block">
                {errors.receipt}
              </span>
            )}
          </div>

          <div className="w-full mt-[20px] flex justify-end">
            <div className="w-[100px]">
              <Button
                label="Done"
                disabled={false}
                handleClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualTransaction;
